"use client";

/**
 * `<AuthCard>` — form card shared between /login and /signup.
 *
 * Reads `callbackUrl` + `error` from the URL (so the OAuth redirect
 * round-trip continues to work) and renders:
 *   - Heading + subhead
 *   - Optional error banner
 *   - Google OAuth button (full-width primary)
 *   - Terms / privacy footnote
 *   - Cross-link to the other page (Sign in <-> Sign up)
 *
 * Only Google OAuth is wired — `src/lib/auth.ts` registers a single
 * `GoogleProvider`, so there is no Credentials flow to expose. The
 * dispatch's "email/password fields" mention is held back until a
 * Credentials provider lands.
 */

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { trackEvent } from "@/lib/posthog";

interface AuthCardProps {
  /** "login" | "signup" — controls heading copy + cross-link target. */
  variant: "login" | "signup";
  heading: string;
  subhead: string;
  /** Default callback URL when ?callbackUrl is missing. */
  defaultCallbackUrl?: string;
}

function errorMessage(error: string | null): string | null {
  if (!error) return null;
  switch (error) {
    case "OAuthSignin":
      return "Error starting sign in. Please try again.";
    case "OAuthCallback":
      return "Error completing sign in. Please try again.";
    case "OAuthAccountNotLinked":
      return "This email is already linked to a different sign-in method.";
    case "AccessDenied":
      return "Access denied. If this seems wrong, contact support.";
    default:
      return "Something went wrong. Please try again.";
  }
}

export function AuthCard({
  variant,
  heading,
  subhead,
  defaultCallbackUrl = "/dashboard",
}: AuthCardProps) {
  const params = useSearchParams();
  const callbackUrl = params.get("callbackUrl") ?? defaultCallbackUrl;
  const error = errorMessage(params.get("error"));
  const [pending, setPending] = useState(false);

  const ctaLabel = variant === "signup" ? "Continue with Google" : "Continue with Google";

  return (
    <div style={{ width: "100%", maxWidth: 420 }}>
      <h1
        style={{
          fontSize: "clamp(30px, 4vw, 40px)",
          fontWeight: 600,
          letterSpacing: "-0.025em",
          lineHeight: 1.05,
          margin: 0,
          color: "var(--color-text)",
        }}
      >
        {heading}
      </h1>
      <p
        style={{
          fontSize: 15,
          color: "var(--color-muted)",
          lineHeight: 1.55,
          marginTop: 14,
          marginBottom: 36,
        }}
      >
        {subhead}
      </p>

      <div
        style={{
          background:
            "linear-gradient(180deg, var(--color-surface-2) 0%, var(--color-surface) 100%)",
          border: "1px solid var(--color-line)",
          borderRadius: 14,
          padding: 28,
          boxShadow: "0 20px 60px -24px rgba(0,0,0,0.5)",
        }}
      >
        {error ? (
          <div
            role="alert"
            style={{
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.30)",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 16,
              fontSize: 13,
              color: "var(--color-red)",
              lineHeight: 1.45,
            }}
          >
            {error}
          </div>
        ) : null}

        <button
          type="button"
          disabled={pending}
          onClick={() => {
            setPending(true);
            trackEvent("signup_started", { variant, callbackUrl });
            // signIn() navigates the page — pending state stays true
            // until the browser unloads. Catches the (rare) early
            // failure to reset.
            signIn("google", { callbackUrl }).catch(() => setPending(false));
          }}
          style={{
            width: "100%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            padding: "13px 16px",
            fontSize: 15,
            fontWeight: 500,
            borderRadius: 10,
            background: pending ? "#E5E7EB" : "#fff",
            color: "#1f2937",
            border: "1px solid rgba(255,255,255,0.08)",
            cursor: pending ? "wait" : "pointer",
            opacity: pending ? 0.85 : 1,
            transition: "background 0.2s, transform 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#F8FAFC";
            e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#fff";
            e.currentTarget.style.transform = "none";
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {pending ? "Redirecting to Google…" : ctaLabel}
        </button>

        <div
          style={{
            marginTop: 18,
            paddingTop: 18,
            borderTop: "1px solid var(--color-line-2)",
            fontSize: 12,
            color: "var(--color-dim)",
            lineHeight: 1.55,
          }}
        >
          By continuing, you agree to our{" "}
          <Link
            href="/terms"
            style={{ color: "var(--color-muted)", textDecoration: "underline" }}
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            href="/privacy"
            style={{ color: "var(--color-muted)", textDecoration: "underline" }}
          >
            Privacy Policy
          </Link>
          . First 3 reports per month are free.
        </div>
      </div>

      <div
        style={{
          marginTop: 24,
          fontSize: 14,
          color: "var(--color-muted)",
        }}
      >
        {variant === "login" ? (
          <>
            New to WhatsTheMoat?{" "}
            <Link
              href="/signup"
              style={{
                color: "var(--color-blue-2)",
                fontWeight: 500,
              }}
            >
              Create an account →
            </Link>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <Link
              href="/login"
              style={{
                color: "var(--color-blue-2)",
                fontWeight: 500,
              }}
            >
              Sign in →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
